import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Battery, BellRing, Check, CheckCircle2, ChevronsLeft, ChevronsRight, Clock, Copy, FileText, Lock, Moon, PhoneIncoming, Signal, Siren, Sparkles, Sun, Wifi } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
type ConsoleTone = 'vexu' | 'red' | 'blue' | 'amber' | 'neutral';
interface MobileFrameProps {
  children: React.ReactNode;
  className?: string;
  onSimulateCall?: () => void;
  onSimulateEmergencyCall?: () => void;
  onSimulateFree?: () => void;
  onSimulateTrial?: () => void;
  onSimulateConsentWithTrial?: () => void;
  onSimulateConsentWithoutTrial?: () => void;
  onSimulateActive?: () => void;
  onSimulateOverage?: () => void;
  onToggleVexuNotification?: () => void;
  vexuNotificationActive?: boolean;
  /** Human-readable state of the sticky Vexu notification, e.g. "Active". */
  vexuNotificationStatus?: string | null;
  /**
   * Overrides the surface behind the device — used when the app is embedded in
   * another experience (e.g. the executive presentation) that paints its own
   * backdrop.
   */
  backdropClassName?: string;
}
const PHONE_WIDTH = 390;
const PHONE_HEIGHT = 844;
const TEST_AGENT_NUMBER = '+1 (765) 632-2092';
function toneClasses(tone: ConsoleTone, isLight: boolean) {
  switch (tone) {
    case 'vexu':
      return isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu';
    case 'red':
      return isLight ? 'bg-red-50 text-red-600' : 'bg-red-500/15 text-red-400';
    case 'blue':
      return isLight ? 'bg-blue-50 text-blue-600' : 'bg-blue-500/15 text-blue-400';
    case 'amber':
      return isLight ? 'bg-amber-50 text-amber-600' : 'bg-amber-500/15 text-amber-400';
    default:
      return isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/[0.07] text-zinc-400';
  }
}
function SectionLabel({
  label,
  isLight
}: {
  label: string;
  isLight: boolean;
}) {
  return <span className={`block text-[8.5px] font-bold uppercase tracking-[0.14em] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1121" data-mp="1xnlv0wwng1km">
      {label}
    </span>;
}
interface ConsoleRowProps {
  isLight: boolean;
  icon: React.ElementType;
  tone: ConsoleTone;
  title: string;
  detail: string;
  badge?: string;
  badgeActive?: boolean;
  onClick: () => void;
}
function ConsoleRow({
  isLight,
  icon: Icon,
  tone,
  title,
  detail,
  badge,
  badgeActive,
  onClick
}: ConsoleRowProps) {
  return <button type="button" onClick={onClick} className={`flex w-full items-center gap-2 rounded-xl border px-2 py-1.5 text-left transition-colors ${isLight ? 'border-slate-200/80 bg-white hover:bg-slate-50' : 'border-white/[0.07] bg-white/[0.03] hover:bg-white/[0.06]'}`} data-id="element-1122" data-mp="cclj86romsqf">
      <span className={`grid h-6 w-6 flex-shrink-0 place-items-center rounded-lg ${toneClasses(tone, isLight)}`} data-id="element-1123" data-mp="lbglx8vipkoz">
        <Icon className="h-3 w-3" data-id="element-1124" data-mp="2n56b4hqpuf19" />
      </span>
      <span className="min-w-0 flex-1" data-id="element-1125" data-mp="2n6he4fcc7irb">
        <span className="flex items-center gap-1.5" data-id="element-1126" data-mp="12m2el5brb18g">
          <span className={`truncate text-[11.5px] font-semibold leading-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1127" data-mp="12ndshxyx1pda">
            {title}
          </span>
          {badge && <span className={`flex-shrink-0 rounded px-1 py-[1px] text-[8px] font-bold uppercase tracking-wider ${badgeActive ? isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu' : isLight ? 'bg-slate-100 text-slate-400' : 'bg-white/[0.07] text-zinc-500'}`} data-id="element-1128" data-mp="supyfazblzi9">
              {badge}
            </span>}
        </span>
        <span className={`mt-0.5 block truncate text-[9.5px] leading-tight ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1129" data-mp="9z87rb7v40dg">
          {detail}
        </span>
      </span>
    </button>;
}
interface ConsoleChipProps {
  isLight: boolean;
  icon: React.ElementType;
  tone: ConsoleTone;
  label: string;
  onClick: () => void;
}
function ConsoleChip({
  isLight,
  icon: Icon,
  tone,
  label,
  onClick
}: ConsoleChipProps) {
  return <button type="button" onClick={onClick} className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-left transition-colors ${isLight ? 'border-slate-200/80 bg-white hover:bg-slate-50' : 'border-white/[0.07] bg-white/[0.03] hover:bg-white/[0.06]'}`} data-id="element-1130" data-mp="577p5dxf8wjm">
      <span className={`grid h-5 w-5 flex-shrink-0 place-items-center rounded-md ${toneClasses(tone, isLight)}`} data-id="element-1131" data-mp="3mfb82nwep97d">
        <Icon className="h-2.5 w-2.5" data-id="element-1132" data-mp="3hocpfhny3z0y" />
      </span>
      <span className={`truncate text-[10.5px] font-semibold ${isLight ? 'text-slate-700' : 'text-zinc-200'}`} data-id="element-1133" data-mp="3hmi4w4gl68xe">
        {label}
      </span>
    </button>;
}
export function MobileFrame({
  children,
  className = '',
  onSimulateCall,
  onSimulateEmergencyCall,
  onSimulateFree,
  onSimulateTrial,
  onSimulateConsentWithTrial,
  onSimulateConsentWithoutTrial,
  onSimulateActive,
  onSimulateOverage,
  onToggleVexuNotification,
  vexuNotificationActive,
  vexuNotificationStatus,
  backdropClassName
}: MobileFrameProps) {
  const {
    isLight,
    toggleTheme
  } = useTheme();
  const [scale, setScale] = useState(1);
  const [consoleOpen, setConsoleOpen] = useState(true);
  const [copiedNumber, setCopiedNumber] = useState(false);
  const calculateScale = useCallback(() => {
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    // Reserve padding around the phone (48px top/bottom, console width right)
    const availableHeight = vh - 96;
    const availableWidth = vw - 300;
    const scaleByHeight = availableHeight / PHONE_HEIGHT;
    const scaleByWidth = availableWidth / PHONE_WIDTH;
    // Use the smaller scale, capped at 1 (never larger than native)
    const newScale = Math.min(scaleByHeight, scaleByWidth, 1);
    // Minimum scale to prevent it from getting too tiny
    setScale(Math.max(newScale, 0.45));
  }, []);
  useEffect(() => {
    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, [calculateScale]);
  const copyTestNumber = () => {
    navigator.clipboard?.writeText(TEST_AGENT_NUMBER).catch(() => undefined);
    setCopiedNumber(true);
    window.setTimeout(() => setCopiedNumber(false), 1600);
  };
  return <div style={{
    height: '100dvh'
  }} className={`w-full flex items-center justify-center font-sans transition-colors duration-500 ${backdropClassName ?? (isLight ? 'bg-stone-100 text-slate-900' : 'bg-black text-white')}`} data-id="element-1134" data-mp="1otoe66zyg8dr">
      {/* Demo Console — preview any state of the app instantly */}
      <div style={{
      position: 'absolute',
      right: 'clamp(12px, 3vw, 32px)',
      top: '50%',
      transform: 'translateY(-50%)'
    }} className="z-50" data-id="element-1135" data-mp="1b44h9ht225cd">
        {consoleOpen ? <div className={`flex max-h-[92dvh] w-[232px] flex-col overflow-hidden rounded-2xl shadow-xl ${isLight ? 'bg-white ring-1 ring-slate-200 shadow-slate-900/[0.06]' : 'bg-zinc-900 ring-1 ring-white/10 shadow-black/40'}`} data-id="element-1136" data-mp="2hilw2909835z">
            {/* Header */}
            <div className="flex-shrink-0 px-3.5 pb-3 pt-3.5" data-id="element-1137" data-mp="2tz4q74l7fpcj">
              <div className="flex items-center justify-between gap-2" data-id="element-1138" data-mp="2g2htn2x5z175">
                <div className="flex min-w-0 items-center gap-1.5" data-id="element-1139" data-mp="2k2i6wk8j16cm">
                  <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-vexu" data-id="element-1140" data-mp="2k4uzdrbwuz3x" />
                  <span className={`truncate text-[12.5px] font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1141" data-mp="37te4jxznyvrs">
                    Demo Console
                  </span>
                </div>
                <button onClick={() => setConsoleOpen(false)} aria-label="Collapse demo console" className={`grid h-6 w-6 flex-shrink-0 place-items-center rounded-lg transition-colors ${isLight ? 'text-slate-400 hover:bg-slate-100 hover:text-slate-600' : 'text-zinc-500 hover:bg-white/5 hover:text-zinc-300'}`} data-id="element-1142" data-mp="331nrvld97vx7">
                  <ChevronsRight className="h-3.5 w-3.5" data-id="element-1143" data-mp="36tdkf7jxz8s0" />
                </button>
              </div>
              <p className={`mt-1 text-[10px] leading-snug ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1144" data-mp="2x1r3cukxafhc">
                Preview any state instantly — nothing here affects a real
                account.
              </p>
            </div>

            <div className={`flex-1 overflow-y-auto border-t px-3 py-3 ${isLight ? 'border-slate-100' : 'border-white/[0.06]'}`} data-id="element-1145" data-mp="3ppn72j8wt7nm">
              {/* Live events */}
              <SectionLabel isLight={isLight} label="Live events" data-id="element-1146" data-mp="3fqe7nav9z0tv" />
              <div className="mt-1.5 space-y-1.5" data-id="element-1147" data-mp="3as4bawxtdu6o">
                {onSimulateCall && <ConsoleRow isLight={isLight} icon={PhoneIncoming} tone="vexu" title="Incoming call" detail="Rings as if someone dialled you" onClick={onSimulateCall} data-id="element-1148" data-mp="24dc2dbymlxp0" />}
                {onSimulateEmergencyCall && <ConsoleRow isLight={isLight} icon={Siren} tone="red" title="Emergency call" detail="Alert → live transcript → flagged log" onClick={onSimulateEmergencyCall} data-id="element-1149" data-mp="ou4g2giyj7k7" />}
                {onToggleVexuNotification && <ConsoleRow isLight={isLight} icon={BellRing} tone={vexuNotificationActive ? 'vexu' : 'neutral'} title="Notification bar" detail="Tap to cycle Active → Busy → DND → Off" badge={vexuNotificationStatus ?? 'Off'} badgeActive={Boolean(vexuNotificationActive)} onClick={onToggleVexuNotification} data-id="element-1150" data-mp="12ndks3vyn4we" />}
              </div>

              {/* Account state */}
              {(onSimulateFree || onSimulateTrial || onSimulateActive || onSimulateOverage) && <>
                  <div className="mt-4" data-id="element-1151" data-mp="6qpwpf23dxs0">
                    <SectionLabel isLight={isLight} label="Account state" data-id="element-1152" data-mp="6rrjuhwduaq3" />
                  </div>
                  <div className="mt-1.5 grid grid-cols-2 gap-1.5" data-id="element-1153" data-mp="borqatx59fhq">
                    {onSimulateFree && <ConsoleChip isLight={isLight} icon={Lock} tone="neutral" label="Free plan" onClick={onSimulateFree} data-id="element-1154" data-mp="gmjlbwt35hq5" />}
                    {onSimulateTrial && <ConsoleChip isLight={isLight} icon={Clock} tone="blue" label="Free trial" onClick={onSimulateTrial} data-id="element-1155" data-mp="zem3jcldl62i" />}
                    {onSimulateActive && <ConsoleChip isLight={isLight} icon={CheckCircle2} tone="vexu" label="Paid & active" onClick={onSimulateActive} data-id="element-1156" data-mp="2ax9he8b0t65v" />}
                    {onSimulateOverage && <ConsoleChip isLight={isLight} icon={AlertTriangle} tone="amber" label="Over limit" onClick={onSimulateOverage} data-id="element-1157" data-mp="2yo6kcvku8t4z" />}
                  </div>
                </>}

              {/* Consent */}
              {(onSimulateConsentWithTrial || onSimulateConsentWithoutTrial) && <>
                  <div className="mt-4" data-id="element-1158" data-mp="4dvmu210xuyz">
                    <SectionLabel isLight={isLight} label="Consent screen" data-id="element-1159" data-mp="99kme5d1nbva" />
                  </div>
                  <div className="mt-1.5 grid grid-cols-2 gap-1.5" data-id="element-1160" data-mp="3rnk572x3fvhq">
                    {onSimulateConsentWithTrial && <ConsoleChip isLight={isLight} icon={Sparkles} tone="blue" label="With trial" onClick={onSimulateConsentWithTrial} data-id="element-1161" data-mp="3vonk9n7bhnhw" />}
                    {onSimulateConsentWithoutTrial && <ConsoleChip isLight={isLight} icon={FileText} tone="neutral" label="No trial" onClick={onSimulateConsentWithoutTrial} data-id="element-1162" data-mp="3cqt5esjichrl" />}
                  </div>
                </>}

              {/* Appearance */}
              <div className="mt-4" data-id="element-1163" data-mp="2rgyilq4bh1c1">
                <SectionLabel isLight={isLight} label="Appearance" data-id="element-1164" data-mp="2rhzx64kjk5yi" />
              </div>
              <div className={`mt-1.5 grid grid-cols-2 gap-1 rounded-xl p-1 ${isLight ? 'bg-slate-100' : 'bg-white/[0.06]'}`} data-id="element-1165" data-mp="358lhzytk2pz1">
                <button onClick={() => !isLight && toggleTheme()} className={`flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-[11px] font-semibold transition-colors ${isLight ? 'bg-white text-slate-900 shadow-sm' : 'text-zinc-400 hover:text-zinc-200'}`} data-id="element-1166" data-mp="3f5r6r0ep64w3">
                  <Sun className="h-3 w-3" data-id="element-1167" data-mp="3j7coaz26f7cm" />
                  Light
                </button>
                <button onClick={() => isLight && toggleTheme()} className={`flex items-center justify-center gap-1.5 rounded-lg py-1.5 text-[11px] font-semibold transition-colors ${!isLight ? 'bg-zinc-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1168" data-mp="1trv6c06d55y1">
                  <Moon className="h-3 w-3" data-id="element-1169" data-mp="1v88ycsql6d21" />
                  Dark
                </button>
              </div>
            </div>

            {/* Test agent footer */}
            <div className={`flex-shrink-0 border-t px-3.5 py-3 ${isLight ? 'border-slate-100 bg-slate-50/60' : 'border-white/[0.06] bg-white/[0.02]'}`} data-id="element-1170" data-mp="258j9m7t29aek">
              <div className="flex items-center justify-between gap-2" data-id="element-1171" data-mp="1cj2nfrw1apqs">
                <div className="min-w-0" data-id="element-1172" data-mp="1ch9hyfx5qbkd">
                  <div className="flex items-center gap-1.5" data-id="element-1173" data-mp="1hi5icjiavfl1">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-vexu" data-id="element-1174" data-mp="1hglzqm968sg3" />
                    <span className={`text-[8.5px] font-bold uppercase tracking-[0.12em] ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-1175" data-mp="1mb8dux6n0z3v">
                      Live test agent
                    </span>
                  </div>
                  <span className={`mt-0.5 block font-mono text-[12px] font-bold leading-none ${isLight ? 'text-slate-800' : 'text-white'}`} data-id="element-1176" data-mp="m8a93m9y12x2">
                    {TEST_AGENT_NUMBER}
                  </span>
                </div>
                <button onClick={copyTestNumber} aria-label="Copy test agent number" className={`grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg transition-colors ${isLight ? 'text-slate-400 ring-1 ring-slate-200 hover:bg-white hover:text-slate-700' : 'text-zinc-500 ring-1 ring-white/10 hover:bg-white/5 hover:text-zinc-200'}`} data-id="element-1177" data-mp="7cb4ilm1lkwo">
                  {copiedNumber ? <Check className="h-3.5 w-3.5 text-vexu" data-id="element-1178" data-mp="3ksimu0rdta3b" /> : <Copy className="h-3.5 w-3.5" data-id="element-1179" data-mp="3fqkwkmkkdf0v" />}
                </button>
              </div>
              <p className={`mt-1.5 text-[9.5px] leading-snug ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1180" data-mp="3br2mklua6oct">
                Call it from any phone to hear the real Vexu agent answer.
              </p>
            </div>
          </div> : <button onClick={() => setConsoleOpen(true)} aria-label="Open demo console" className={`flex items-center gap-1.5 rounded-full px-3 py-2 shadow-lg transition-all ${isLight ? 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50' : 'bg-zinc-900 text-zinc-300 ring-1 ring-white/10 hover:bg-zinc-800'}`} data-id="element-1181" data-mp="2s5sm2icydngx">
            <ChevronsLeft className="h-3.5 w-3.5" data-id="element-1182" data-mp="3b3v76m2dr09g" />
            <span className="text-[11px] font-semibold" data-id="element-1183" data-mp="3b33cddglx6k8">Demo</span>
          </button>}
      </div>

      {/* Scaled Phone Container */}
      <div style={{
      width: PHONE_WIDTH,
      height: PHONE_HEIGHT,
      transform: `scale(${scale})`,
      transformOrigin: 'center center',
      flexShrink: 0
    }} data-id="element-1184" data-mp="7m38ypxnhk2t">
        {/* Phone Frame — outer shell */}
        <div className={`relative w-full h-full shadow-2xl rounded-[40px] border-[8px] transition-colors duration-500 ${isLight ? 'bg-[#e8e8e6] border-[#e8e8e6] ring-1 ring-black/[0.04] shadow-black/[0.08]' : 'bg-zinc-900 border-zinc-900 ring-1 ring-white/10'}`} data-id="element-1185" data-mp="lq2521s0ao34">
          {/* Inner Screen Mask — clips all content to the inner rounded area */}
          <div className="absolute inset-0 rounded-[32px] overflow-hidden" data-id="element-1186" data-mp="2u2b4lfgxagw">
            {/* Status Bar */}
            <div className={`absolute top-0 left-0 right-0 h-12 px-6 flex items-center justify-between z-50 transition-colors duration-500 ${isLight ? 'text-slate-800 bg-white/60 backdrop-blur-2xl' : 'text-white bg-dark/80 backdrop-blur-2xl border-b border-white/5'}`} data-id="element-1187" data-mp="3uyo3avgn5odl">
              <div className="text-sm font-semibold ml-2" data-id="element-1188" data-mp="1o52fk2vilut5">9:41</div>
              <div className="flex items-center gap-1.5" data-id="element-1189" data-mp="1j82cm0typ869">
                <Signal className="w-4 h-4" data-id="element-1190" data-mp="1j9n7jrk6ut4n" />
                <Wifi className="w-4 h-4" data-id="element-1191" data-mp="3dok8g02pqu45" />
                <Battery className="w-5 h-5" data-id="element-1192" data-mp="3dlo8653e7tcj" />
              </div>
            </div>

            {/* Dynamic Island Placeholder */}
            <div className={`absolute top-2 left-1/2 -translate-x-1/2 w-[120px] h-[35px] rounded-full z-50 pointer-events-none transition-colors duration-500 ${isLight ? 'bg-black ring-1 ring-black/5' : 'bg-black ring-1 ring-white/5'}`} data-id="element-1193" data-mp="3mopeo6w67mgq" />

            {/* Content Area */}
            <div className={`w-full h-full overflow-y-auto pt-12 pb-safe transition-colors duration-500 ${isLight ? 'app-gradient-bg-light' : 'app-gradient-bg'} ${className}`} data-id="element-1194" data-mp="57pcz5o8wc03">
              {children}
            </div>

            {/* Home Indicator */}
            <div className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-32 h-1 rounded-full z-50 pointer-events-none transition-colors duration-500 ${isLight ? 'bg-slate-900/20' : 'bg-white/20'}`} data-id="element-1195" data-mp="e69q7rb1dtgx" />
          </div>
        </div>
      </div>
    </div>;
}